import { GOOGLE_MAPS_API_KEY } from './config.js';

/**
 * ONE LOADER, MIRRORING THE ONE frontend-client ALREADY HAS.
 *
 * `frontend-client/resources/views/components/google-maps-component.blade.php`
 * is the single Blade component behind every Maps surface on the public site —
 * search results, the listing About-map, the wizard pin step. This is that
 * loader in React: same script URL, same libraries, same map style id, so the
 * workspace map looks like the rest of the product and there is one Google
 * Cloud project to keep referrer restrictions correct on.
 *
 * THE THREE TRAPS ARE DOCUMENTED IN `frontend-client/CLAUDE.md` and two of them
 * bite here too:
 *
 *   1. `google.maps` is populated BEFORE its constructors are attached, so
 *      there is a real window where `window.google.maps` is truthy and
 *      `google.maps.Map` is still undefined. This reached production on
 *      2026-05-16. Every guard below tests `google.maps.Map`, never
 *      `google.maps`.
 *   2. A failure must be LOGGED, never swallowed. The public site turned its
 *      catch-all into a `console.error` after a silent `ReferenceError` spent a
 *      release rendering "Map unavailable" with the real cause invisible.
 *
 * The third — an unbounded poll waiting for somebody else's map instance — is
 * specific to the About-map's accordion and has no analogue here.
 */

const SCRIPT_ID = 'sa-google-maps';

/**
 * The style id the public site's maps are drawn with. Reused so a producer does
 * not meet a differently-coloured map inside the same product; it is also
 * REQUIRED for advanced markers, which is what draws the numbered pins.
 */
export const MAP_STYLE_ID = '1009a2e723730a0b4e92ef4e';

/** How long to wait for the constructors after the script's own `onload`. */
const READY_TIMEOUT_MS = 10_000;
const POLL_MS = 100;

let inFlight: Promise<void> | null = null;

export function mapsConfigured(): boolean {
  return GOOGLE_MAPS_API_KEY !== '';
}

/**
 * Resolve once `google.maps.Map` is genuinely callable.
 *
 * Idempotent and single-flight: the map tab can mount, unmount and remount
 * while the script is still arriving, and appending it twice would leave two
 * copies racing to populate the same namespace.
 */
export function loadGoogleMaps(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.Map) return Promise.resolve();
  if (inFlight) return inFlight;

  if (!mapsConfigured()) {
    return Promise.reject(new Error('GOOGLE_MAPS_API_KEY is not configured'));
  }

  inFlight = new Promise<void>((resolve, reject) => {
    const whenReady = (startedAt: number): void => {
      if (window.google?.maps?.Map) {
        resolve();
        return;
      }
      if (Date.now() - startedAt > READY_TIMEOUT_MS) {
        // BOUNDED, and loud. An indefinite poll on a real Maps outage spins a
        // tab forever and reports nothing.
        const error = new Error('google.maps.Map never appeared');
        console.error('[google-maps] constructors never attached after 10s');
        reject(error);
        return;
      }
      setTimeout(() => {
        whenReady(startedAt);
      }, POLL_MS);
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      whenReady(Date.now());
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    // `loading=async` is what Google asks for and what the public site sends;
    // `marker` is the library the numbered pins need.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_MAPS_API_KEY,
    )}&libraries=geometry,marker&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      whenReady(Date.now());
    };
    script.onerror = () => {
      console.error('[google-maps] script failed to load');
      reject(new Error('Google Maps script failed to load'));
    };

    document.head.appendChild(script);
  });

  // A failed load must not poison every later attempt — a producer who switches
  // tabs and comes back should get another try.
  inFlight = inFlight.catch((error: unknown) => {
    inFlight = null;
    throw error;
  });

  return inFlight;
}
